import { FieldError } from "react-hook-form";
import { forwardRef, SyntheticEvent, useCallback, useState } from "react";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { AutoComplete, MenuItem, Select, autoCompleteClasses } from "@kaltura/ds-react-components";
import { DropdownOption } from "@kaltura/mediaspace-shared-types";
import SmartContainer from "./smart-container/SmartContainer";
import { FormFieldError } from "@kaltura/mediaspace-shared-ui/form";
import { uniqueId } from "lodash";
import { useUtilityClasses } from "./fieldClasses";
import SmartFormLabel from "./smart-form-label/SmartFormLabel";
import styled from "@emotion/styled";
import clsx from "clsx";
import { AutocompleteChangeDetails, AutocompleteChangeReason } from "@mui/material";

export interface Props {
    /**
     * title to show next to the form element
     */
    title: string;
    /**
     * name attribute for the underlying input field
     */
    name?: string;
    /**
     * id attribute for the underlying input field
     */
    id?: string;
    /**
     * initial value when page loads, should match one of the options' values.
     * use when component is unbound
     */
    defaultValue?: string;
    /**
     * current component value, should match one of the options' values.
     * use when component is bound
     */
    value?: string;
    /**
     * list of options to show in the dropdown
     */
    options: DropdownOption[];
    /**
     * error
     */
    error?: FieldError;
    /**
     * text to show when value is erroneous
     */
    errorMessage?: string;
    /**
     * is value required?
     */
    required?: boolean;
    /**
     * text to show before any value was selected
     */
    placeholder?: string;
    /**
     * change handler
     * @param value new selected value
     */
    onChange: (value: string | undefined) => void;
    /**
     * blur handler
     */
    onBlur?: () => void;
    /**
     * allow users to type and filter values by text
     */
    autocompleteMatch?: boolean;
    /**
     * allow users to add new values
     */
    createable?: boolean;
    /**
     * form element disabled
     */
    isDisabled?: boolean;
    /**
     * class to add to root element
     */
    className?: string;
    /**
     * data test id for testing purposes
     * */
    dataTestId?: string;
}

const StyledAutoComplete = styled(AutoComplete)(({ theme }) => ({
    [`& .${autoCompleteClasses.input}`]: {
        ...theme.kaltura.typography.buttonLabel1,
    },
    [`& .${autoCompleteClasses.input}.${autoCompleteClasses.input}::placeholder`]: {
        color: theme.kaltura.palette.tone1,
    },
}));

/**
 * select input field for site registration form
 */
const SelectField = forwardRef<HTMLInputElement, Props>((props, selectRef) => {
    const {
        title,
        name,
        id = uniqueId("input_"),
        defaultValue,
        value,
        options = [],
        error,
        errorMessage,
        required = false,
        placeholder = "",
        onChange,
        onBlur,
        autocompleteMatch = false,
        createable = false,
        isDisabled = false,
        className,
        dataTestId,
    } = props;

    const classes = useUtilityClasses();

    /**
     * find an option in the list by its value.
     * needed when using the autocomplete component.
     * @param value the value of the option we are looking for
     */
    const getOption = (value: string | undefined) => {
        // If value is undefined then the component is used as an uncontrolled input, so return undefined
        if (value === undefined) {
            return undefined;
        }

        // If value is empty, but not undefined, then the component is used as a controlled input, so return null
        if (!value) {
            return null;
        }

        if (options) {
            // check in options list
            const found = options.find((option: DropdownOption) => option.value === value);
            if (found) {
                return found;
            }
        }

        // create new value - only if creatable
        if (createable) {
            return {
                label: value,
                value: value,
            };
        }

        // when all else had failed
        return null;
    };

    const shouldOpenCondition = useCallback(
        (event?: SyntheticEvent<HTMLButtonElement>): boolean => {
            const elementText = event?.currentTarget?.value;
            const inputEventValidation = !!elementText && elementText.length > 2;
            const clickEventValidation =
                event?.type === "click" || event?.type === "mousedown" || event?.type === "keydown";
            return inputEventValidation || clickEventValidation;
        },
        []
    );

    const defaultOption = getOption(defaultValue);
    const selectedOption = getOption(value);

    const [creatableText, setCreatableText] = useState<string>();

    const handleAutocompleteChange = (
        event: SyntheticEvent,
        value: string | DropdownOption,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<string | DropdownOption>
    ) => {
        if (!value) {
            // selection cleared (for example, using backspace)
            setCreatableText(undefined);
            onChange("");
        }
        else if (typeof value === "string") {
            // this is from creatable, only fire onChange if valid selection
            if (reason === "createOption") {
                if (details?.option) {
                    // user selected the text they typed (clicked enter).
                    setCreatableText(undefined);
                    onChange(details.option as string); //  cast for TS, in this case details.option will be a string
                }
                else {
                    // otherwise, save typed value
                    setCreatableText(value);
                    onChange(value);
                }
            }
        }
        else {
            // "regular" selection
            setCreatableText(undefined);
            onChange(value.value);
        }
    };

    const handleAutocompleteBlur = () => {
        onBlur?.();
        // user moved to another field, "save" current typed text.
        if (creatableText) {
            onChange(creatableText);
        }
    };

    return (
        <SmartContainer className={clsx(classes.root, className)}>
            <SmartFormLabel htmlFor={id} className={classes.label} required={required} text={title} />
            <span className={classes.input} data-testid={dataTestId}>
                {autocompleteMatch ? (
                    <StyledAutoComplete
                        id={id}
                        hasError={!!error}
                        name={name}
                        placeholder={placeholder}
                        options={options}
                        onChange={handleAutocompleteChange}
                        onBlur={handleAutocompleteBlur}
                        shouldOpen={shouldOpenCondition}
                        userfilterOptions={{ matchFrom: "start" }}
                        defaultValue={defaultOption}
                        value={selectedOption}
                        aria-invalid={error ? "true" : "false"}
                        disabled={isDisabled}
                        inputRef={selectRef}
                        multiple={false}
                        required={required}
                        getOptionLabel={(option) => {
                            if (option) {
                                if (typeof option === "string") {
                                    return option;
                                }
                                if ("label" in option) {
                                    return option.label;
                                }
                            }
                            return "";
                        }}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        fullWidth={true}
                        freeSolo={createable}
                        selectOnFocus={createable}
                        handleHomeEndKeys={createable}
                        noOptionsText={translate("No options")}
                    />
                ) : (
                    <Select
                        id={id}
                        name={name}
                        error={!!error}
                        aria-label={title}
                        onChange={(event) => onChange(event.target.value)}
                        onBlur={onBlur}
                        placeholder={placeholder}
                        defaultValue={defaultValue}
                        value={value}
                        aria-invalid={error ? "true" : "false"}
                        disabled={isDisabled}
                        ref={selectRef}
                        fullWidth={true}
                        required={required}
                    >
                        {options.map((option, index) => (
                            <MenuItem key={`${id}_item_${index}`} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                )}
                <div aria-live="polite">
                    {error && errorMessage && <FormFieldError>{errorMessage}</FormFieldError>}
                    {error && !errorMessage && <FormFieldError>{translate("%1 is required", [title])}</FormFieldError>}
                </div>
            </span>
        </SmartContainer>
    );
});
export default SelectField;
